import { useNotificationToast } from '@context/NotificationToastContext'
import {
  IAiReceptionistSettings,
  IContact,
  AppointmentRequest,
  AppointmentResponse,
} from '@dataTypes/index'
import { useLocation, useNavigate } from 'react-router-dom'
import { useClinicOperatories, useClinicProviders } from '@hooks'
import { useGlobalLoadingActions } from '@hooks/useGlobalLoading'
import { useSettingsSubpageLoading } from '@pages/Settings/SettingsSubpageGate.component'
import {
  fetchAiReceptionistAppointmentsApi,
  fetchAiReceptionistPatientsApi,
  fetchAiReceptionistSettingsApi,
  previewAiReceptionistVoiceApi,
  saveAiReceptionistSettingsApi,
  type AiReceptionistAppointmentRow,
  type AiReceptionistPatientRow,
  fetchPatientById,
  deleteAppointmentById,
  getAppointmentById,
  updateAppointmentById,
  updateAppointmentStatusById,
} from '@services/api'
import { AppointmentStatus } from '@dataTypes/appointment.type'
import { APPT_STATUS_MENU_ITEMS } from '@constants/AppointmentPage'
import {
  fetchAllAppointmentTypes,
  fetchAllContacts,
  removePatient,
  updatePatient,
} from '@store/actions'
import { useAppDispatch, useAppSelector } from '@store/hooks'
import {
  createFailedToast,
  createSuccessToast,
} from '@utils/notificationToast.util'
import {
  SetStateAction,
  Dispatch,
  useState,
  useMemo,
  useCallback,
  useEffect,
  useRef,
} from 'react'
import useClinicChange from '../shared/useClinicChange'
import { Patient } from '@dataTypes/PatientInfo.types'
import { setSelectedPatient } from '@store/reducers/patientmanagement.reducer'
import { setActiveSidePanel } from '@store/reducers/uiSlice.reducer'
import useContact from '@components/pages/Messages/MessageCenterContact/useContact'
import { getFullName } from '@utils/fullname.util'
import { FilterSelection } from '@enamelai/design-system'
import {
  formatAppointmentDate,
  formatAppointmentTimeRange,
} from './AiReceptionistAppointmentsTab.component'
import { emptyAiReceptionistSettings } from '@constants/index'
import { getAiReceptionistFieldErrors } from '@utils/ai.util'

export interface IReceptionistHooks {
  activeTab: string
  appointmentFilterOptions: FilterSelection
  appointmentsLoading: boolean
  appointmentSearchFilters: FilterSelection
  appointmentSearchQuery: string
  appointmentToDelete: AiReceptionistAppointmentRow | null
  clearAppointmentSearch: () => void
  clearPatientSearch: () => void
  confirmDeleteAppointment: () => Promise<void>
  confirmDeletePatient: () => Promise<void>
  currentAppointment: AppointmentResponse | null
  editPatData: Patient | null
  emailRecipient: string | null
  filteredAppointments: AiReceptionistAppointmentRow[]
  filteredPatients: AiReceptionistPatientRow[]
  hasAppointmentSearch: boolean
  hasChanges: boolean
  handleCloseAddPatientModal: () => void
  handleCloseEditAppointmentModal: () => void
  handleCloseSelectContactModal: () => void
  handleReset: () => void
  handleSave: () => Promise<void>
  handleSaveAppointment: (
    appointmentDetails: Partial<AppointmentRequest>,
  ) => void
  handleSavePatient: (patientData: Patient) => Promise<void>
  hasPatientSearch: boolean
  loading: boolean
  patientsLoading: boolean
  settingsReady: boolean
  patientSearchFilters: FilterSelection
  patientSearchQuery: string
  patientStatusFilters: FilterSelection
  patientToDelete: Patient | null
  playPreview: (feature?: string) => Promise<void>
  previewing: boolean
  runAppointmentAction: (
    appointment: AiReceptionistAppointmentRow,
    action: 'view' | 'edit' | 'delete',
  ) => Promise<void>
  handleUpdateAppointmentStatus: (
    appointment: AiReceptionistAppointmentRow,
    status: AppointmentStatus,
  ) => Promise<void>
  runPatientAction: (patientId: string, action: string) => Promise<void>
  selectedContact: IContact | null
  serviceOptions: { value: string; label: string }[]
  setActiveTab: Dispatch<SetStateAction<string>>
  setAppointmentSearchFilters: Dispatch<SetStateAction<FilterSelection>>
  setAppointmentSearchQuery: Dispatch<SetStateAction<string>>
  setEmailRecipient: Dispatch<SetStateAction<string | null>>
  setPatientSearchFilters: Dispatch<SetStateAction<FilterSelection>>
  setPatientSearchQuery: Dispatch<SetStateAction<string>>
  setSettings: Dispatch<SetStateAction<IAiReceptionistSettings>>
  setShowConfirmDeleteAppointment: Dispatch<SetStateAction<boolean>>
  setShowConfirmDeleteModal: Dispatch<SetStateAction<boolean>>
  settings: IAiReceptionistSettings
  showAddPatientModal: boolean
  showConfirmDeleteAppointment: boolean
  showConfirmDeleteModal: boolean
  showEditAppointmentModal: boolean
  showSelectContactModal: boolean
}

const LOADING_CONTAINER = 'settings-ai-receptionist'

const IGNORED_CHANGE_FIELDS = new Set([
  'ai_receptionist_settings_id',
  'clinic_id',
  'created_by',
  'updated_by',
  'date_time_created_at',
  'date_time_updated_at',
])

const normalizeSettingValue = (value: unknown): string => {
  if (Array.isArray(value)) {
    return JSON.stringify([...value].map((item) => String(item ?? '')).sort())
  }
  if (typeof value === 'boolean') return value ? 'true' : 'false'
  if (typeof value === 'number') {
    return Number.isFinite(value) ? String(value) : ''
  }
  if (value == null) return ''
  return String(value)
}

const cloneAiReceptionistSettings = (
  value: IAiReceptionistSettings,
): IAiReceptionistSettings => ({
  ...value,
  controls: [...(value.controls ?? [])],
  languages: [...(value.languages ?? [])],
  verification_enabled: value.verification_enabled !== false,
  verification_required_for: [...(value.verification_required_for ?? [])],
  verification_fields: [...(value.verification_fields ?? [])],
  escalation_scenarios: [...(value.escalation_scenarios ?? [])],
  services: [...(value.services ?? [])],
  channels: [...(value.channels ?? [])],
})

const hasAiReceptionistSettingChanges = (
  current: IAiReceptionistSettings,
  baseline: IAiReceptionistSettings,
): boolean => {
  const keys = Object.keys({ ...current, ...baseline })
  for (const key of keys) {
    if (IGNORED_CHANGE_FIELDS.has(key)) continue
    const currentValue = current[key as keyof IAiReceptionistSettings]
    const baselineValue = baseline[key as keyof IAiReceptionistSettings]
    if (
      normalizeSettingValue(currentValue) !==
      normalizeSettingValue(baselineValue)
    ) {
      return true
    }
  }
  return false
}

export const useAIReceptionist = (): IReceptionistHooks => {
  useClinicOperatories()
  useClinicProviders()
  const dispatch = useAppDispatch()
  const navigate = useNavigate()
  const location = useLocation()
  const { showToast } = useNotificationToast()
  const { showLoading, hideLoading } = useGlobalLoadingActions()
  const appointmentTypes = useAppSelector((state) => state.appointmentType.data)
  const selectedClinic = useAppSelector((s) => s.clinic.selectedClinic)
  const clinicId = selectedClinic?.clinic_id ?? ''
  const [activeTab, setActiveTab] = useState<string>('configurations')
  const [loading, setLoading] = useState<boolean>(false)
  const [settingsReady, setSettingsReady] = useState(false)
  const settingsGen = useRef(0)

  // Configurations tab
  const [previewing, setPreviewing] = useState(false)
  const [settings, setSettings] = useState<IAiReceptionistSettings>(
    emptyAiReceptionistSettings,
  )
  const [savedSettings, setSavedSettings] = useState<IAiReceptionistSettings>(
    emptyAiReceptionistSettings,
  )

  // Patients tab
  const {
    filteredContacts,
    handleCloseSelectContactModal,
    handleSendMessage,
    selectedContact,
    showSelectContactModal,
    handlePhoneAction,
  } = useContact()
  const contacts = useAppSelector((state) => state.contacts.data)
  const [patients, setPatients] = useState<AiReceptionistPatientRow[]>([])
  const [patientsLoading, setPatientsLoading] = useState(false)
  const [editPatData, setEditPatData] = useState<Patient | null>(null)
  const [showAddPatientModal, setShowAddPatientModal] = useState(false)
  const [showConfirmDeleteModal, setShowConfirmDeleteModal] = useState(false)
  const [patientToDelete, setPatientToDelete] = useState<Patient | null>(null)
  const [emailRecipient, setEmailRecipient] = useState<string | null>(null)
  const [patientSearchQuery, setPatientSearchQuery] = useState('')
  const [patientSearchFilters, setPatientSearchFilters] =
    useState<FilterSelection>({})

  // Appointments tab
  const [appointments, setAppointments] = useState<
    AiReceptionistAppointmentRow[]
  >([])
  const [appointmentsLoading, setAppointmentsLoading] = useState(false)
  const [appointmentSearchQuery, setAppointmentSearchQuery] = useState('')
  const [appointmentSearchFilters, setAppointmentSearchFilters] =
    useState<FilterSelection>({})
  const [appointmentToDelete, setAppointmentToDelete] =
    useState<AiReceptionistAppointmentRow | null>(null)
  const [showConfirmDeleteAppointment, setShowConfirmDeleteAppointment] =
    useState(false)
  const [currentAppointment, setCurrentAppointment] =
    useState<AppointmentResponse | null>(null)
  const [showEditAppointmentModal, setShowEditAppointmentModal] =
    useState(false)

  useClinicChange(clinicId, () => {
    settingsGen.current += 1
    setSettingsReady(false)
    setLoading(true)
    setPatients([])
    setAppointments([])
    setPatientsLoading(true)
    setAppointmentsLoading(true)
  })

  useSettingsSubpageLoading(
    !settingsReady ||
      (activeTab === 'patients' && patientsLoading) ||
      (activeTab === 'appointments' && appointmentsLoading),
  )

  const selectTab = useCallback((value: SetStateAction<string>) => {
    setActiveTab((current) => {
      const next = typeof value === 'function' ? value(current) : value
      if (next !== current) {
        if (next === 'patients') setPatientsLoading(true)
        if (next === 'appointments') setAppointmentsLoading(true)
      }
      return next
    })
  }, [])

  useEffect(() => {
    if (!clinicId) {
      setLoading(false)
      setSettingsReady(true)
      return
    }

    const gen = ++settingsGen.current
    let cancelled = false
    setSettingsReady(false)
    setLoading(true)
    dispatch(fetchAllAppointmentTypes())
    fetchAiReceptionistSettingsApi(clinicId)
      .then((response) => {
        if (cancelled) return
        const loaded = cloneAiReceptionistSettings(
          response.data.ai_receptionist_settings ??
            emptyAiReceptionistSettings(),
        )
        setSettings(loaded)
        setSavedSettings(cloneAiReceptionistSettings(loaded))
      })
      .catch(() => {
        if (cancelled) return
        showToast(
          createFailedToast(
            'AI receptionist',
            'Could not load this clinic setup.',
          ),
        )
      })
      .finally(() => {
        if (cancelled || settingsGen.current !== gen) return
        setLoading(false)
        setSettingsReady(true)
      })

    return () => {
      cancelled = true
    }
  }, [clinicId, dispatch, showToast])

  useEffect(() => {
    const params = new URLSearchParams(location.search)
    const tab = params.get('tab')
    if (tab && ['patients', 'appointments', 'configurations'].includes(tab)) {
      selectTab(tab)
    }
  }, [location.search, selectTab])

  const loadPatients = useCallback(async (): Promise<void> => {
    if (!clinicId) return
    setPatients([])
    setPatientsLoading(true)
    try {
      const response = await fetchAiReceptionistPatientsApi(clinicId)
      setPatients(response.data.patients ?? [])
    } catch {
      setPatients([])
      showToast(
        createFailedToast(
          'AI receptionist',
          'Could not load AI-created patients.',
        ),
      )
    } finally {
      setPatientsLoading(false)
    }
  }, [clinicId, showToast])

  const loadAppointments = useCallback(async (): Promise<void> => {
    if (!clinicId) return
    setAppointments([])
    setAppointmentsLoading(true)
    try {
      const response = await fetchAiReceptionistAppointmentsApi(clinicId)
      setAppointments(
        response.data.appointments
          ? response.data.appointments.map((appointment) => ({
              ...appointment,
              id: appointment.appointment_id,
            }))
          : [],
      )
    } catch {
      setAppointments([])
      showToast(
        createFailedToast('AI receptionist', 'Could not load AI appointments.'),
      )
    } finally {
      setAppointmentsLoading(false)
    }
  }, [clinicId, showToast])

  useEffect(() => {
    if (activeTab === 'patients') void loadPatients()
    if (activeTab === 'appointments') void loadAppointments()
  }, [activeTab, loadAppointments, loadPatients])

  const serviceOptions = useMemo(() => {
    const byName = new Map<string, { value: string; label: string }>()
    for (const appointmentType of appointmentTypes) {
      const subcategories = Array.isArray(
        appointmentType.appointmenttypesubcategories,
      )
        ? appointmentType.appointmenttypesubcategories
        : []
      for (const subcategory of subcategories) {
        const name = String(subcategory?.name ?? '').trim()
        if (!name || byName.has(name)) continue
        byName.set(name, { value: name, label: name })
      }
    }
    return Array.from(byName.values())
  }, [appointmentTypes])

  const playPreview = async (feature?: string): Promise<void> => {
    setPreviewing(true)
    try {
      let text: undefined | string
      if (feature === 'voice_style') {
        text = `Hello, this is ${
          settings.name.trim().length
            ? settings.name
            : `${settings.voice_style} voice.`
        }.`
      }
      const blob = await previewAiReceptionistVoiceApi(
        settings.voice_style,
        settings.voice_tone,
        text,
      )
      const url = URL.createObjectURL(blob)
      const audio = new Audio(url)
      audio.onended = () => URL.revokeObjectURL(url)
      await audio.play()
    } catch {
      showToast(
        createFailedToast(
          'Voice preview',
          'The selected voice could not be played.',
        ),
      )
    } finally {
      setPreviewing(false)
    }
  }

  const handleSave = useCallback(async (): Promise<void> => {
    if (!clinicId) return
    if (Object.keys(getAiReceptionistFieldErrors(settings)).length > 0) return
    const channels = Array.isArray(settings.channels) ? settings.channels : []
    setLoading(true)
    showLoading({ container: LOADING_CONTAINER })

    try {
      const controls = Array.isArray(settings.controls) ? settings.controls : []
      const payload = {
        ...settings,
        channels,
        schedule_approved_types: controls.includes('new_booking'),
        reschedule_cancel:
          controls.includes('reschedule') || controls.includes('cancel'),
        answer_faqs: controls.includes('faq'),
        create_callback_tasks: controls.includes('callback'),
      }
      await saveAiReceptionistSettingsApi(clinicId, payload)
      const saved = cloneAiReceptionistSettings(payload)
      setSettings(saved)
      setSavedSettings(cloneAiReceptionistSettings(saved))
      showToast(createSuccessToast('AI receptionist', 'Successfully saved.'))
    } catch {
      showToast(
        createFailedToast('AI receptionist', 'Could not save this setting.'),
      )
    } finally {
      setLoading(false)
      hideLoading(LOADING_CONTAINER)
    }
  }, [clinicId, hideLoading, settings, showLoading, showToast])

  const handleReset = useCallback((): void => {
    setSettings(cloneAiReceptionistSettings(savedSettings))
  }, [savedSettings])

  const hasChanges = useMemo(
    () => hasAiReceptionistSettingChanges(settings, savedSettings),
    [savedSettings, settings],
  )

  const loadPatientRecord = async (
    patientId: string,
  ): Promise<Patient | null> => {
    if (!patientId) return null
    try {
      const response = await fetchPatientById(patientId, true)
      const patient = response.data?.patient
      if (!patient) {
        showToast(createFailedToast('Patient', 'Could not load this patient.'))
        return null
      }
      return patient as unknown as Patient
    } catch {
      showToast(createFailedToast('Patient', 'Could not load this patient.'))
      return null
    }
  }

  const findPatientContact = (patient: Patient): IContact | undefined => {
    const patientContact = filteredContacts.find(
      (contact) => contact.patient_id === patient.patient_id,
    )
    if (!patientContact) {
      showToast(
        createFailedToast(
          'Contact not found',
          `${getFullName(patient)} has no contact record yet.`,
        ),
      )
    }
    return patientContact
  }

  const onShowPatientInfo = (patient: Patient): void => {
    const contact = contacts?.find(
      (item) => item.patient_id === patient.patient_id,
    )
    dispatch(setActiveSidePanel('main'))
    dispatch(setSelectedPatient({ patient, contact }))
  }

  const handleCloseAddPatientModal = (): void => {
    setEditPatData(null)
    setShowAddPatientModal(false)
  }

  const handleSavePatient = async (patientData: Patient): Promise<void> => {
    if (!patientData.patient_id) return
    await dispatch(updatePatient(patientData)).then(async () => {
      await dispatch(fetchAllContacts())
      await loadPatients()
    })
  }

  const confirmDeletePatient = useCallback(async (): Promise<void> => {
    if (!patientToDelete) return
    showLoading({ container: '.patient-delete-modal' })
    try {
      await dispatch(removePatient(patientToDelete.patient_id)).unwrap()
      await loadPatients()
      setTimeout(() => {
        hideLoading('.patient-delete-modal')
        showToast(
          createSuccessToast(
            'Patient deleted successfully',
            'Patient has been deleted successfully.',
          ),
        )
      }, 1000)
    } catch {
      setTimeout(() => {
        hideLoading('.patient-delete-modal')
        showToast(
          createFailedToast(
            'Patient not deleted',
            'Patient not deleted. Please try again.',
          ),
        )
      }, 1000)
    } finally {
      setShowConfirmDeleteModal(false)
    }
  }, [
    dispatch,
    hideLoading,
    loadPatients,
    patientToDelete,
    showLoading,
    showToast,
  ])

  const runPatientAction = async (
    patientId: string,
    action: string,
  ): Promise<void> => {
    const patient = await loadPatientRecord(patientId)
    if (!patient) return
    if (action === 'view') {
      onShowPatientInfo(patient)
      return
    }
    if (action === 'edit') {
      setEditPatData(patient)
      setShowAddPatientModal(true)
      return
    }
    if (action === 'delete') {
      setPatientToDelete(patient)
      setShowConfirmDeleteModal(true)
      return
    }
    if (action === 'mail') {
      if (patient.email) setEmailRecipient(patient.email)
      return
    }
    const patientContact = findPatientContact(patient)
    if (!patientContact) return
    if (action === 'phone') {
      handlePhoneAction(patientContact)
      return
    }
    handleSendMessage(patientContact)
    navigate('/message')
  }

  const patientStatusFilters = useMemo(
    () => ({
      status: Array.from(
        new Set(patients.map((patient) => patient.status).filter(Boolean)),
      ),
    }),
    [patients],
  )

  const filteredPatients = useMemo(() => {
    const query = patientSearchQuery.trim().toLowerCase()
    const selectedStatuses = patientSearchFilters.status ?? []
    return patients.filter((patient) => {
      const matchesQuery =
        !query || patient.patient_name.toLowerCase().includes(query)
      const matchesStatus =
        selectedStatuses.length === 0 ||
        selectedStatuses.includes(patient.status)
      return matchesQuery && matchesStatus
    })
  }, [patientSearchFilters.status, patientSearchQuery, patients])

  const hasPatientSearch =
    patientSearchQuery.trim().length > 0 ||
    (patientSearchFilters.status?.length ?? 0) > 0

  const clearPatientSearch = (): void => {
    setPatientSearchFilters({})
    setPatientSearchQuery('')
  }

  const uniqueValues = (values: string[]): string[] =>
    Array.from(new Set(values.filter(Boolean)))

  const appointmentFilterOptions = useMemo(
    () => ({
      status: uniqueValues(
        appointments.map((appointment) => appointment.status),
      ),
      provider: uniqueValues(
        appointments.map((appointment) => appointment.provider_name),
      ),
      subcategory: uniqueValues(
        appointments.map((appointment) => appointment.subcategory),
      ),
    }),
    [appointments],
  )

  const filteredAppointments = useMemo(() => {
    const query = appointmentSearchQuery.trim().toLowerCase()
    const selectedStatuses = appointmentSearchFilters.status ?? []
    const selectedProviders = appointmentSearchFilters.provider ?? []
    const selectedSubcategories = appointmentSearchFilters.subcategory ?? []
    return appointments.filter((appointment) => {
      const matchesQuery =
        !query ||
        [
          appointment.patient_name,
          appointment.provider_name,
          appointment.date,
          formatAppointmentDate(appointment.date),
          appointment.time,
          formatAppointmentTimeRange(appointment.time, appointment.time_end),
          appointment.subcategory,
          appointment.status,
        ]
          .join(' ')
          .toLowerCase()
          .includes(query)
      const matchesStatus =
        selectedStatuses.length === 0 ||
        selectedStatuses.includes(appointment.status)
      const matchesProvider =
        selectedProviders.length === 0 ||
        selectedProviders.includes(appointment.provider_name)
      const matchesSubcategory =
        selectedSubcategories.length === 0 ||
        selectedSubcategories.includes(appointment.subcategory)
      return (
        matchesQuery && matchesStatus && matchesProvider && matchesSubcategory
      )
    })
  }, [appointmentSearchFilters, appointmentSearchQuery, appointments])

  const hasAppointmentSearch =
    appointmentSearchQuery.trim().length > 0 ||
    Object.values(appointmentSearchFilters).some((values) => values.length > 0)

  const clearAppointmentSearch = (): void => {
    setAppointmentSearchFilters({})
    setAppointmentSearchQuery('')
  }

  const handleCloseEditAppointmentModal = (): void => {
    setShowEditAppointmentModal(false)
    setCurrentAppointment(null)
  }

  const handleSaveAppointment = (
    appointmentDetails: Partial<AppointmentRequest>,
  ): void => {
    if (!appointmentDetails.appointment_id) return
    void updateAppointmentById(
      appointmentDetails.appointment_id,
      appointmentDetails,
    ).then(() => {
      void loadAppointments()
    })
  }

  const confirmDeleteAppointment = useCallback(async (): Promise<void> => {
    if (!appointmentToDelete) return
    showLoading({ container: LOADING_CONTAINER })
    try {
      const response = await deleteAppointmentById(
        appointmentToDelete.appointment_id,
      )
      if (response.statusCode !== 200) {
        throw new Error(response.message)
      }
      await loadAppointments()
      showToast(
        createSuccessToast(
          'Appointment deleted successfully',
          'Appointment has been deleted successfully.',
        ),
      )
    } catch {
      showToast(
        createFailedToast(
          'Appointment not deleted',
          'Appointment not deleted. Please try again.',
        ),
      )
    } finally {
      hideLoading(LOADING_CONTAINER)
      setShowConfirmDeleteAppointment(false)
    }
  }, [
    appointmentToDelete,
    hideLoading,
    loadAppointments,
    showLoading,
    showToast,
  ])

  const runAppointmentAction = async (
    appointment: AiReceptionistAppointmentRow,
    action: 'view' | 'edit' | 'delete',
  ): Promise<void> => {
    if (action === 'delete') {
      setAppointmentToDelete(appointment)
      setShowConfirmDeleteAppointment(true)
      return
    }
    try {
      const response = await getAppointmentById(appointment.appointment_id)
      const loaded = response.data?.appointment
      if (!loaded) {
        showToast(
          createFailedToast('Appointment', 'Could not load this appointment.'),
        )
        return
      }
      setCurrentAppointment(loaded)
      setShowEditAppointmentModal(true)
    } catch {
      showToast(
        createFailedToast('Appointment', 'Could not load this appointment.'),
      )
    }
  }

  const handleUpdateAppointmentStatus = useCallback(
    async (
      appointment: AiReceptionistAppointmentRow,
      status: AppointmentStatus,
    ): Promise<void> => {
      if (appointment.appt_status === status) return
      const statusLabel =
        APPT_STATUS_MENU_ITEMS[status as keyof typeof APPT_STATUS_MENU_ITEMS]
          ?.text ?? 'status'
      try {
        const response = await updateAppointmentStatusById(
          appointment.appointment_id,
          status,
        )
        if (response.statusCode !== 200) {
          throw new Error(response.message)
        }
        setAppointments((prev) =>
          prev.map((row) =>
            row.appointment_id === appointment.appointment_id
              ? {
                  ...row,
                  id: row.appointment_id,
                  appt_status: status,
                  status: statusLabel,
                }
              : row,
          ),
        )
        showToast(
          createSuccessToast(
            'Appointment status updated',
            `Status changed to ${statusLabel}.`,
          ),
        )
      } catch {
        showToast(
          createFailedToast(
            'Appointment status',
            'Could not update appointment status. Please try again.',
          ),
        )
        await loadAppointments()
      }
    },
    [loadAppointments, showToast],
  )

  return {
    activeTab,
    appointmentFilterOptions,
    appointmentsLoading,
    appointmentSearchFilters,
    appointmentSearchQuery,
    appointmentToDelete,
    clearAppointmentSearch,
    clearPatientSearch,
    confirmDeleteAppointment,
    confirmDeletePatient,
    currentAppointment,
    editPatData,
    emailRecipient,
    filteredAppointments,
    filteredPatients,
    hasAppointmentSearch,
    hasChanges,
    handleCloseAddPatientModal,
    handleCloseEditAppointmentModal,
    handleCloseSelectContactModal,
    handleReset,
    handleSave,
    handleSaveAppointment,
    handleSavePatient,
    hasPatientSearch,
    loading,
    patientsLoading,
    settingsReady,
    patientSearchFilters,
    patientSearchQuery,
    patientStatusFilters,
    patientToDelete,
    playPreview,
    previewing,
    runAppointmentAction,
    handleUpdateAppointmentStatus,
    runPatientAction,
    selectedContact,
    serviceOptions,
    setActiveTab: selectTab,
    setAppointmentSearchFilters,
    setAppointmentSearchQuery,
    setEmailRecipient,
    setPatientSearchFilters,
    setPatientSearchQuery,
    setSettings,
    setShowConfirmDeleteAppointment,
    setShowConfirmDeleteModal,
    settings,
    showAddPatientModal,
    showConfirmDeleteAppointment,
    showConfirmDeleteModal,
    showEditAppointmentModal,
    showSelectContactModal,
  }
}
